// CaseActionsPanel — asignar, cambiar estado, agregar seguimiento, cerrar.
import React, { useEffect, useState } from 'react';
import { UserPlus, Play, Plus, XCircle, Loader2 } from 'lucide-react';
import { casosApi } from '../../../lib/dashboardApi';
import { MOTIVOS_CIERRE, ESTADOS } from '../../../lib/dashboardVocab';

const ESTADOS_TRANSICIONES = ['pendiente_primera_accion', 'en_seguimiento', 'pendiente_revision'];

const ROL_LABEL = {
  directivo: 'Directivo',
  encargado_convivencia: 'Encargado de Convivencia',
  orientador: 'Orientador',
};

function Panel({ title, children, tone = '#5346E6', testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5EAF5] p-4" data-testid={testid}>
      <h3 className="font-extrabold text-[#0B1B5E] text-sm mb-3" style={{ color: tone }}>{title}</h3>
      {children}
    </div>
  );
}

function Btn({ children, onClick, loading, disabled, variant = 'primary', testid }) {
  const style = variant === 'danger'
    ? 'bg-[#8A1616] hover:bg-[#B91C1C] text-white'
    : variant === 'ghost'
      ? 'bg-[#F4F7FE] hover:bg-[#EEF0FE] text-[#0B1B5E]'
      : 'bg-[#0B1B5E] hover:bg-[#5346E6] text-white';
  return (
    <button
      type="button"
      className={`w-full inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold transition ${style} ${(disabled || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testid}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : children}
    </button>
  );
}

export default function CaseActionsPanel({ caso, puedeAsignar, puedeCerrar, puedeGestionar, onChange }) {
  const [asignResponsableId, setAsignResponsableId] = useState('');
  const [asignables, setAsignables] = useState([]);
  const [asignLoading, setAsignLoading] = useState(false);
  const [asignError, setAsignError] = useState('');
  const [seguimiento, setSeguimiento] = useState({ descripcion: '', proxima_revision: '' });
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [cierre, setCierre] = useState({ motivo: '', detalle: '' });
  const [loading, setLoading] = useState({ a: false, e: false, s: false, c: false });
  const [msg, setMsg] = useState({ ok: '', err: '' });

  const clear = () => setMsg({ ok: '', err: '' });

  const disabledAll = caso.estado === 'cerrado';

  // Sprint 5 · Anexo P0.2 — carga usuarios elegibles al montar (si puede asignar y caso abierto).
  useEffect(() => {
    if (!puedeAsignar || disabledAll || !caso.colegio_id) return;
    let ignore = false;
    setAsignLoading(true);
    setAsignError('');
    casosApi
      .asignables(caso.colegio_id)
      .then((r) => { if (!ignore) setAsignables(r?.usuarios || []); })
      .catch((e) => { if (!ignore) setAsignError(e.message || 'No se pudieron cargar los responsables.'); })
      .finally(() => { if (!ignore) setAsignLoading(false); });
    return () => { ignore = true; };
  }, [puedeAsignar, disabledAll, caso.colegio_id]);

  async function submitAsignar() {
    clear();
    if (!asignResponsableId) { setMsg({ err: 'Selecciona un responsable de la lista.' }); return; }
    setLoading((s) => ({ ...s, a: true }));
    try {
      await casosApi.asignar(caso.id, { responsable_id: asignResponsableId });
      setMsg({ ok: 'Responsable asignado.' });
      setAsignResponsableId('');
      onChange?.();
    } catch (e) { setMsg({ err: e.message }); }
    setLoading((s) => ({ ...s, a: false }));
  }

  async function submitEstado() {
    clear();
    if (!nuevoEstado) { setMsg({ err: 'Selecciona un estado.' }); return; }
    setLoading((s) => ({ ...s, e: true }));
    try {
      await casosApi.estado(caso.id, { estado: nuevoEstado });
      setMsg({ ok: `Estado cambiado a ${ESTADOS[nuevoEstado]}.` });
      setNuevoEstado('');
      onChange?.();
    } catch (e) { setMsg({ err: e.message }); }
    setLoading((s) => ({ ...s, e: false }));
  }

  async function submitSeguimiento() {
    clear();
    if (!seguimiento.descripcion.trim()) { setMsg({ err: 'Escribe una descripción.' }); return; }
    setLoading((s) => ({ ...s, s: true }));
    try {
      await casosApi.seguimiento(caso.id, {
        descripcion: seguimiento.descripcion.trim(),
        proxima_revision: seguimiento.proxima_revision || undefined,
        tipo: 'seguimiento',
      });
      setMsg({ ok: 'Seguimiento agregado.' });
      setSeguimiento({ descripcion: '', proxima_revision: '' });
      onChange?.();
    } catch (e) { setMsg({ err: e.message }); }
    setLoading((s) => ({ ...s, s: false }));
  }

  async function submitCierre() {
    clear();
    if (!cierre.motivo) { setMsg({ err: 'Selecciona un motivo.' }); return; }
    if (cierre.motivo === 'otro' && !cierre.detalle.trim()) { setMsg({ err: 'El motivo "otro" requiere detalle.' }); return; }
    setLoading((s) => ({ ...s, c: true }));
    try {
      await casosApi.cerrar(caso.id, { motivo: cierre.motivo, detalle: cierre.detalle || undefined });
      setMsg({ ok: 'Caso cerrado.' });
      onChange?.();
    } catch (e) { setMsg({ err: e.message }); }
    setLoading((s) => ({ ...s, c: false }));
  }

  if (disabledAll) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5EAF5] p-5 text-sm text-[#475472] space-y-2" data-testid="acciones-caso-cerrado">
        <h3 className="font-extrabold text-[#0B1B5E]">Caso cerrado</h3>
        <p>Motivo: <strong>{caso.motivo_cierre}</strong></p>
        {caso.motivo_cierre_detalle && <p className="text-xs">Detalle: {caso.motivo_cierre_detalle}</p>}
        <p className="text-xs text-[#94A3B8]">No se pueden agregar acciones a un caso cerrado.</p>
      </div>
    );
  }

  if (!puedeGestionar) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5EAF5] p-5 text-sm text-[#475472]" data-testid="acciones-sin-permiso">
        <h3 className="font-extrabold text-[#0B1B5E] mb-2">Acciones</h3>
        <p className="text-xs">Tu rol no permite gestionar este caso.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="case-actions-panel">
      {msg.ok && <div className="bg-[#DFF6EC] border border-[#B5E8CD] text-[#0F6C4B] rounded-lg px-3 py-2 text-xs" data-testid="msg-ok">{msg.ok}</div>}
      {msg.err && <div className="bg-[#FADBD8] border border-[#F5B7B1] text-[#8A1616] rounded-lg px-3 py-2 text-xs" data-testid="msg-err">{msg.err}</div>}

      {puedeAsignar && (
        <Panel title="Asignar responsable" testid="panel-asignar">
          {asignError && (
            <div className="text-[11px] text-[#8A1616] mb-2" data-testid="asignables-error">{asignError}</div>
          )}
          <select
            value={asignResponsableId}
            onChange={(e) => setAsignResponsableId(e.target.value)}
            disabled={asignLoading || asignables.length === 0}
            className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2 disabled:bg-[#F4F7FE]"
            data-testid="select-responsable"
          >
            <option value="">
              {asignLoading
                ? 'Cargando usuarios elegibles…'
                : asignables.length === 0
                  ? 'No hay usuarios elegibles en este colegio'
                  : 'Selecciona un responsable…'}
            </option>
            {asignables.map((u) => (
              <option key={u.id} value={u.id} data-testid={`opt-responsable-${u.id.slice(0, 8)}`}>
                {u.nombre_completo} · {ROL_LABEL[u.rol] || u.rol}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-[#94A3B8] mb-2">
            Solo se muestran perfiles activos del mismo colegio con rol Directivo, Encargado de Convivencia u Orientador.
          </p>
          <Btn onClick={submitAsignar} loading={loading.a} disabled={!asignResponsableId} testid="btn-submit-asignar">
            <UserPlus className="w-3.5 h-3.5" /> Asignar
          </Btn>
        </Panel>
      )}

      <Panel title="Cambiar estado" testid="panel-estado">
        <select
          value={nuevoEstado}
          onChange={(e) => setNuevoEstado(e.target.value)}
          className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2"
          data-testid="select-nuevo-estado"
        >
          <option value="">Selecciona estado…</option>
          {ESTADOS_TRANSICIONES.map((s) => (
            <option key={s} value={s}>{ESTADOS[s]}</option>
          ))}
        </select>
        <Btn onClick={submitEstado} loading={loading.e} testid="btn-submit-estado" variant="ghost">
          <Play className="w-3.5 h-3.5" /> Aplicar
        </Btn>
      </Panel>

      <Panel title="Agregar seguimiento" testid="panel-seguimiento">
        <textarea
          rows={3}
          placeholder="Describe la acción realizada o el seguimiento…"
          value={seguimiento.descripcion}
          onChange={(e) => setSeguimiento((s) => ({ ...s, descripcion: e.target.value }))}
          className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2"
          data-testid="input-seguimiento-desc"
        />
        <label className="block text-[11px] text-[#475472] mb-1">Próxima revisión (opcional)</label>
        <input
          type="date"
          value={seguimiento.proxima_revision}
          onChange={(e) => setSeguimiento((s) => ({ ...s, proxima_revision: e.target.value }))}
          className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2"
          data-testid="input-seguimiento-fecha"
        />
        <Btn onClick={submitSeguimiento} loading={loading.s} testid="btn-submit-seguimiento">
          <Plus className="w-3.5 h-3.5" /> Registrar
        </Btn>
      </Panel>

      {puedeCerrar && (
        <Panel title="Cerrar caso" tone="#8A1616" testid="panel-cerrar">
          <select
            value={cierre.motivo}
            onChange={(e) => setCierre((c) => ({ ...c, motivo: e.target.value }))}
            className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2"
            data-testid="select-motivo-cierre"
          >
            <option value="">Selecciona motivo…</option>
            {MOTIVOS_CIERRE.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
          {cierre.motivo === 'otro' && (
            <textarea
              rows={2}
              placeholder="Detalle del motivo (obligatorio)"
              value={cierre.detalle}
              onChange={(e) => setCierre((c) => ({ ...c, detalle: e.target.value }))}
              className="w-full rounded-lg border border-[#E5EAF5] px-2 py-1.5 text-xs mb-2"
              data-testid="input-motivo-detalle"
            />
          )}
          <Btn onClick={submitCierre} loading={loading.c} testid="btn-submit-cerrar" variant="danger">
            <XCircle className="w-3.5 h-3.5" /> Cerrar caso
          </Btn>
        </Panel>
      )}
    </div>
  );
}
